// R5.3 (round 2) — HOW A FACT'S **TYPED VALUE** BECOMES SOMETHING A PERSON CAN READ AND EDIT.
//
// §4.1 requires Layer 1 to render "each fact's typed value". Through round 1 the surface rendered a fact only
// when it had APPLICANT PROSE to show — the quoted span or the string the applicant typed — which meant that a
// fact whose value is a date, a count, a boolean, or a closed §2 enum rendered NOTHING AT ALL, and that codebook
// §2.1 rule 5 ("the user can edit any fact") could not be honoured for those fields either, because a field with
// no label cannot be offered an editor. The build review named that a blocker, and this module is the fix.
//
// WHY IT IS A SHARED, PURE MODULE AND NOT A COMPONENT TABLE. Three separate things have to agree about the same
// closed sets — what fields exist, what enum members each carries, and which of them accept `not_stated`:
//
//   * the SURFACE, which renders the value and builds the editor's options;
//   * `factEdit.ts`'s `EDITABLE_FIELDS`, which validates what the PATCH route accepts;
//   * the EXTRACTOR's own `SPANNABLE_FIELD_NAMES`, which decides what a fact may be minted for.
//
// A component-local table would be a fourth opinion, and the drift test at the bottom of `factLabels.test.ts`
// exists precisely because the first three already disagreed once. Everything here is derived from, or checked
// against, the registries that already exist; no set is re-typed.
//
// NO STRING IS INVENTED HERE. Every label is an Appendix A id — the ratified A.5 rows where one already existed,
// and the provisional A.5-P block otherwise (`rubricCopy.ts`). T-COPY-1 is satisfied by construction: this module
// returns IDS, never text.
import type { CopyStringId } from './rubricCopy';
import { SIGNAL_TYPES, STORABLE_ENTRY_TYPES, type EntryType, type SignalType } from './factLedger';

/**
 * WHAT KIND OF THING THE VALUE IS — which decides both how it renders and which editor it gets.
 *
 * `opaque` is a real member, not a fallback: §2.2's `organization_context_id` is an anonymous distinctness token
 * whose ONLY permitted use is equality comparison (§4.2, §11.2). It is not display text, it is not the
 * applicant's prose, and printing `ORG-3` at somebody would be the internal-vocabulary leak §0 forbids. It
 * therefore renders no value and offers no editor, and it says so by carrying a kind rather than by being
 * quietly missing from a table.
 */
export type FactValueKind = 'text' | 'enum' | 'date_partial' | 'integer' | 'number' | 'boolean' | 'opaque';

export interface FieldPresentation {
  /** The Appendix A id for this field's name. */
  labelId: CopyStringId;
  kind: FactValueKind;
  /** `enum` only — the closed member set, each mapped to its own registered label id. */
  values?: Readonly<Record<string, CopyStringId>>;
  /**
   * 2026-08-23 — members that RENDER but may not be OFFERED for editing. `values` stays TOTAL over the enum, so a
   * stored value of any kind still resolves a label on the facts screen; these are the ones the EDITOR must leave
   * out because a write of them would be refused by the database. Today that is `entry_type`'s `education` and
   * `language`, whose column CHECK has not been widened (see `STORABLE_ENTRY_TYPES`). Offering an option whose
   * PATCH takes a 500 is worse than not offering it.
   */
  not_editable_values?: readonly string[];
  /** Whether §2's schema for this field admits the literal `not_stated` (§2.1 rule 2: absence is a status). */
  allowsNotStated: boolean;
  /** The one §2.2 clause field stored multi-line. Decides `<textarea>` vs `<input>` (§1.5's newline contract). */
  multiline?: boolean;
}

const vals = <T extends Record<string, CopyStringId>>(v: T): T => Object.freeze(v);

/* ================================================================================================================
 * THE CLOSED ENUM MEMBER SETS (§2.2, §2.4, §2.5, §2.6), each member with its registered label id.
 *
 * Written out rather than derived from the TYPE, because a TypeScript union is erased at runtime and a select
 * needs the members at runtime. `factLabels.test.ts` checks each set against the validator in `factEdit.ts` and
 * against the CHECK constraints the migration declares, so "written out" does not mean "unchecked".
 * ============================================================================================================== */

const ENTRY_TYPE = vals({
  clinical: 'CS-VAL-ENTRY-TYPE-CLINICAL',
  research_project: 'CS-VAL-ENTRY-TYPE-RESEARCH-PROJECT',
  scholarly_work: 'CS-VAL-ENTRY-TYPE-SCHOLARLY-WORK',
  teaching: 'CS-VAL-ENTRY-TYPE-TEACHING',
  leadership_role: 'CS-VAL-ENTRY-TYPE-LEADERSHIP-ROLE',
  service: 'CS-VAL-ENTRY-TYPE-SERVICE',
  employment: 'CS-VAL-ENTRY-TYPE-EMPLOYMENT',
  award: 'CS-VAL-ENTRY-TYPE-AWARD',
  certification: 'CS-VAL-ENTRY-TYPE-CERTIFICATION',
  membership: 'CS-VAL-ENTRY-TYPE-MEMBERSHIP',
  education: 'CS-VAL-ENTRY-TYPE-EDUCATION',
  language: 'CS-VAL-ENTRY-TYPE-LANGUAGE',
  other: 'CS-VAL-ENTRY-TYPE-OTHER',
} as const);

/**
 * THE TYPE-LEVEL TOTALITY BRIDGE for `entry_type` (2026-08-23).
 *
 * `EntryType` is a union, and a union is erased at runtime — which is exactly how `education` and `language` were
 * added to the union, the extraction prompt and the §2.2b profile table while the label registry and the PATCH
 * validator kept the old eleven, so the new members rendered BLANK and a correction to them was refused. Annotating
 * the map as `Record<EntryType, CopyStringId>` makes that failure a COMPILE error: the next member added to the
 * union cannot be left unlabelled. `factLabels.test.ts` carries the runtime half (every member is also a value the
 * server's own validator accepts).
 */
export const ENTRY_TYPE_LABELS: Readonly<Record<EntryType, CopyStringId>> = ENTRY_TYPE;

/** The editor's option list: `ENTRY_TYPE` restricted to the values the schema will accept (`STORABLE_ENTRY_TYPES`). */
export const STORABLE_ENTRY_TYPE_LABELS: Readonly<Record<string, CopyStringId>> = Object.fromEntries(
  STORABLE_ENTRY_TYPES.map((t) => [t, ENTRY_TYPE[t]]),
);

/** The members an editor may offer for `field`: its `values` minus anything the schema cannot store. */
export function editableValues(p: FieldPresentation): Readonly<Record<string, CopyStringId>> {
  const gated = new Set(p.not_editable_values ?? []);
  return Object.fromEntries(Object.entries(p.values ?? {}).filter(([v]) => !gated.has(v)));
}

const SOURCE_SECTION = vals({
  research: 'CS-VAL-SOURCE-SECTION-RESEARCH',
  publications: 'CS-VAL-SOURCE-SECTION-PUBLICATIONS',
  education: 'CS-VAL-SOURCE-SECTION-EDUCATION',
  work: 'CS-VAL-SOURCE-SECTION-WORK',
  volunteer: 'CS-VAL-SOURCE-SECTION-VOLUNTEER',
  leadership: 'CS-VAL-SOURCE-SECTION-LEADERSHIP',
  teaching: 'CS-VAL-SOURCE-SECTION-TEACHING',
  awards: 'CS-VAL-SOURCE-SECTION-AWARDS',
  presentations: 'CS-VAL-SOURCE-SECTION-PRESENTATIONS',
  other: 'CS-VAL-SOURCE-SECTION-OTHER',
  unlabelled: 'CS-VAL-SOURCE-SECTION-UNLABELLED',
} as const);

const FREQUENCY = vals({
  one_off: 'CS-VAL-FREQUENCY-ONE-OFF',
  weekly: 'CS-VAL-FREQUENCY-WEEKLY',
  monthly: 'CS-VAL-FREQUENCY-MONTHLY',
  per_term: 'CS-VAL-FREQUENCY-PER-TERM',
  irregular: 'CS-VAL-FREQUENCY-IRREGULAR',
  continuous: 'CS-VAL-FREQUENCY-CONTINUOUS',
} as const);

const SUPERVISION_LEVEL = vals({
  observer_shadowing: 'CS-VAL-SUPERVISION-OBSERVER-SHADOWING',
  supervised_student: 'CS-VAL-SUPERVISION-SUPERVISED-STUDENT',
  supervised_trainee_postgrad: 'CS-VAL-SUPERVISION-SUPERVISED-TRAINEE-POSTGRAD',
  stated_independent: 'CS-VAL-SUPERVISION-STATED-INDEPENDENT',
  non_clinical: 'CS-VAL-SUPERVISION-NON-CLINICAL',
} as const);

const TRAINING_STAGE = vals({
  pre_med: 'CS-VAL-TRAINING-STAGE-PRE-MED',
  undergrad_med: 'CS-VAL-TRAINING-STAGE-UNDERGRAD-MED',
  postgrad: 'CS-VAL-TRAINING-STAGE-POSTGRAD',
  post_licensure: 'CS-VAL-TRAINING-STAGE-POST-LICENSURE',
  non_medical: 'CS-VAL-TRAINING-STAGE-NON-MEDICAL',
} as const);

const ACTIVITY_STATUS = vals({
  completed: 'CS-VAL-ACTIVITY-STATUS-COMPLETED',
  under_way: 'CS-VAL-ACTIVITY-STATUS-UNDER-WAY',
} as const);

const WORK_TYPE = vals({
  empirical_study: 'CS-VAL-WORK-TYPE-EMPIRICAL-STUDY',
  review_synthesis: 'CS-VAL-WORK-TYPE-REVIEW-SYNTHESIS',
  case_report: 'CS-VAL-WORK-TYPE-CASE-REPORT',
  qi_project: 'CS-VAL-WORK-TYPE-QI-PROJECT',
  thesis: 'CS-VAL-WORK-TYPE-THESIS',
  educational_material: 'CS-VAL-WORK-TYPE-EDUCATIONAL-MATERIAL',
  commentary_editorial: 'CS-VAL-WORK-TYPE-COMMENTARY-EDITORIAL',
  other_scholarly: 'CS-VAL-WORK-TYPE-OTHER-SCHOLARLY',
} as const);

const AUTHORSHIP_POSITION = vals({
  first: 'CS-VAL-AUTHORSHIP-FIRST',
  co_first_stated: 'CS-VAL-AUTHORSHIP-CO-FIRST-STATED',
  middle: 'CS-VAL-AUTHORSHIP-MIDDLE',
  last_senior: 'CS-VAL-AUTHORSHIP-LAST-SENIOR',
  corresponding_stated: 'CS-VAL-AUTHORSHIP-CORRESPONDING-STATED',
  group_consortium: 'CS-VAL-AUTHORSHIP-GROUP-CONSORTIUM',
  position_not_determinable: 'CS-VAL-AUTHORSHIP-POSITION-NOT-DETERMINABLE',
} as const);

const AUTHOR_ORDER_CONVENTION = vals({
  contribution_ordered_assumed: 'CS-VAL-AUTHOR-ORDER-CONTRIBUTION-ORDERED-ASSUMED',
  alphabetical_apparent: 'CS-VAL-AUTHOR-ORDER-ALPHABETICAL-APPARENT',
  consortium: 'CS-VAL-AUTHOR-ORDER-CONSORTIUM',
  unknown: 'CS-VAL-AUTHOR-ORDER-UNKNOWN',
} as const);

const RESULT_DIRECTION = vals({
  positive: 'CS-VAL-RESULT-DIRECTION-POSITIVE',
  null_or_negative: 'CS-VAL-RESULT-DIRECTION-NULL-OR-NEGATIVE',
  mixed: 'CS-VAL-RESULT-DIRECTION-MIXED',
} as const);

const RECOGNITION_TYPE = vals({
  grant: 'CS-VAL-RECOGNITION-TYPE-GRANT',
  scholarship: 'CS-VAL-RECOGNITION-TYPE-SCHOLARSHIP',
  prize_award: 'CS-VAL-RECOGNITION-TYPE-PRIZE-AWARD',
  honour_society: 'CS-VAL-RECOGNITION-TYPE-HONOUR-SOCIETY',
  competitive_selection: 'CS-VAL-RECOGNITION-TYPE-COMPETITIVE-SELECTION',
  not_determinable: 'CS-VAL-RECOGNITION-TYPE-NOT-DETERMINABLE',
} as const);

const AWARD_SCOPE = vals({
  course_level: 'CS-VAL-AWARD-SCOPE-COURSE-LEVEL',
  institutional: 'CS-VAL-AWARD-SCOPE-INSTITUTIONAL',
  provincial_regional: 'CS-VAL-AWARD-SCOPE-PROVINCIAL-REGIONAL',
  national: 'CS-VAL-AWARD-SCOPE-NATIONAL',
  international: 'CS-VAL-AWARD-SCOPE-INTERNATIONAL',
} as const);

const SELECTION_BASIS = vals({
  merit_competitive: 'CS-VAL-SELECTION-BASIS-MERIT-COMPETITIVE',
  need_based: 'CS-VAL-SELECTION-BASIS-NEED-BASED',
  nomination: 'CS-VAL-SELECTION-BASIS-NOMINATION',
  participation: 'CS-VAL-SELECTION-BASIS-PARTICIPATION',
} as const);

const EXPOSURE_TYPE = vals({
  observership: 'CS-VAL-EXPOSURE-OBSERVERSHIP',
  elective: 'CS-VAL-EXPOSURE-ELECTIVE',
  core_rotation: 'CS-VAL-EXPOSURE-CORE-ROTATION',
  selective: 'CS-VAL-EXPOSURE-SELECTIVE',
  employment_clinical: 'CS-VAL-EXPOSURE-EMPLOYMENT-CLINICAL',
  volunteer_clinical: 'CS-VAL-EXPOSURE-VOLUNTEER-CLINICAL',
  simulation: 'CS-VAL-EXPOSURE-SIMULATION',
} as const);

const PATIENT_CONTACT = vals({
  stated_direct: 'CS-VAL-PATIENT-CONTACT-STATED-DIRECT',
  stated_observational: 'CS-VAL-PATIENT-CONTACT-STATED-OBSERVATIONAL',
} as const);

const LICENCE_STATUS = vals({
  unlicensed: 'CS-VAL-LICENCE-UNLICENSED',
  student_registration: 'CS-VAL-LICENCE-STUDENT-REGISTRATION',
  provisional_licence: 'CS-VAL-LICENCE-PROVISIONAL',
  full_licence: 'CS-VAL-LICENCE-FULL',
} as const);

/** Every closed enum set this module knows, by the field that carries it — for the drift test's own sweep. */
export const ENUM_SETS: Readonly<Record<string, Readonly<Record<string, CopyStringId>>>> = Object.freeze({
  entry_type: ENTRY_TYPE,
  source_section: SOURCE_SECTION,
  frequency: FREQUENCY,
  supervision_level: SUPERVISION_LEVEL,
  training_stage_at_time: TRAINING_STAGE,
  activity_status: ACTIVITY_STATUS,
  'scholarly.work_type': WORK_TYPE,
  'scholarly.authorship_position': AUTHORSHIP_POSITION,
  'scholarly.author_order_convention': AUTHOR_ORDER_CONVENTION,
  'scholarly.result_direction': RESULT_DIRECTION,
  'recognition.recognition_type': RECOGNITION_TYPE,
  'recognition.award_scope': AWARD_SCOPE,
  'recognition.selection_basis_stated': SELECTION_BASIS,
  'clinical.exposure_type': EXPOSURE_TYPE,
  'clinical.patient_contact_stated': PATIENT_CONTACT,
  'clinical.licence_status_at_time': LICENCE_STATUS,
});

/* ================================================================================================================
 * THE FIELD TABLE
 * ============================================================================================================== */

const field = (
  labelId: CopyStringId,
  kind: FactValueKind,
  allowsNotStated: boolean,
  extra: { values?: Readonly<Record<string, CopyStringId>>; not_editable_values?: readonly string[]; multiline?: boolean } = {},
): FieldPresentation => Object.freeze({ labelId, kind, allowsNotStated, ...extra });

export const FIELD_PRESENTATION: ReadonlyMap<string, FieldPresentation> = new Map<string, FieldPresentation>([
  /* ---- §2.2 entry record ---------------------------------------------------------------------------------- */
  // `entry_type` has NO `not_stated` member — `other` is its ratified catch-all (§2.2).
  // THE EDITOR OFFERS ONLY WHAT THE DATABASE WILL STORE (2026-08-23). `ENTRY_TYPE` labels every member of the
  // union — including ones whose migration has not landed — because the FACTS SCREEN must be able to render a
  // stored value of any kind. The EDITOR is a write path, so it is filtered by the schema gate: offering an
  // option whose PATCH D1 refuses is offering the applicant a 500. See `STORABLE_ENTRY_TYPES`.
  ['entry_type', field('CS-FIELD-ENTRY-TYPE', 'enum', false, {
    // TOTAL for rendering; the two gated members are named so the editor can drop them (see `not_editable_values`).
    values: ENTRY_TYPE,
    not_editable_values: (Object.keys(ENTRY_TYPE) as EntryType[]).filter((t) => !STORABLE_ENTRY_TYPES.includes(t)),
  })],
  ['date_start', field('CS-FIELD-DATE-START', 'date_partial', true)],
  ['date_end', field('CS-FIELD-DATE-END', 'date_partial', true)],
  ['is_ongoing', field('CS-FIELD-IS-ONGOING', 'boolean', true)],
  ['duration_stated', field('CS-FIELD-DURATION-STATED', 'text', true)],
  ['frequency', field('CS-FIELD-FREQUENCY', 'enum', true, { values: FREQUENCY })],
  ['role_title', field('CS-FIELD-ROLE-TITLE', 'text', true)],
  // THE ONE MULTI-LINE CLAUSE FIELD (§2.2, §1.5). `STATEMENT_FIELDS` in `factEdit.ts` is the same set, and the
  // drift test asserts the two agree — a field the normalizer treats as multi-line but the UI opens in a
  // single-line input is exactly the divergence the build review rejected.
  ['responsibility_statement', field('CS-FIELD-RESPONSIBILITY-STATEMENT', 'text', true, { multiline: true })],
  ['supervision_level', field('CS-FIELD-SUPERVISION-LEVEL', 'enum', true, { values: SUPERVISION_LEVEL })],
  ['jurisdiction', field('CS-FIELD-JURISDICTION', 'text', true)],
  ['training_stage_at_time', field('CS-FIELD-TRAINING-STAGE', 'enum', true, { values: TRAINING_STAGE })],
  ['team_size', field('CS-FIELD-TEAM-SIZE', 'integer', true)],
  ['institution', field('CS-FIELD-INSTITUTION', 'text', true)],
  ['activity_status', field('CS-FIELD-ACTIVITY-STATUS', 'enum', true, { values: ACTIVITY_STATUS })],
  // `unlabelled` is `source_section`'s own catch-all; there is no `not_stated` member (§2.2).
  ['source_section', field('CS-FIELD-SOURCE-SECTION', 'enum', false, { values: SOURCE_SECTION })],
  ['section_heading', field('CS-FIELD-SECTION-HEADING', 'text', false)],
  ['raw_text', field('CS-FIELD-RAW-TEXT', 'text', false, { multiline: true })],
  ['organization_stated', field('CS-FIELD-ORGANIZATION-STATED', 'text', true)],
  ['topic_tag', field('CS-FIELD-TOPIC-TAG', 'text', false)],
  // §2.2/§4.2/§11.2 — an OPAQUE distinctness token. Equality comparison only; never display text, never editable.
  ['organization_context_id', field('CS-FIELD-ORGANIZATION-CONTEXT', 'opaque', true)],

  /* ---- §2.2 activity-status wording evidence (the `signals.*` family) --------------------------------------- */
  ['signals.completion_clause', field('CS-FIELD-COMPLETION-CLAUSE', 'text', false)],
  ['signals.continuation_clause', field('CS-FIELD-CONTINUATION-CLAUSE', 'text', false)],
  ['signals.past_tense_evidence', field('CS-FIELD-PAST-TENSE-EVIDENCE', 'text', false)],
  ['signals.continuing_present_tense_evidence', field('CS-FIELD-CONTINUING-PRESENT-EVIDENCE', 'text', false)],

  /* ---- §2.4 scholarly work ---------------------------------------------------------------------------------- */
  ['scholarly.work_type', field('CS-FIELD-WORK-TYPE', 'enum', false, { values: WORK_TYPE })],
  ['scholarly.title_stated', field('CS-FIELD-WORK-TITLE', 'text', true)],
  ['scholarly.authorship_position', field('CS-FIELD-AUTHORSHIP-POSITION', 'enum', true, { values: AUTHORSHIP_POSITION })],
  ['scholarly.author_count', field('CS-FIELD-AUTHOR-COUNT', 'integer', true)],
  ['scholarly.author_order_convention', field('CS-FIELD-AUTHOR-ORDER-CONVENTION', 'enum', false, { values: AUTHOR_ORDER_CONVENTION })],
  ['scholarly.contribution_statement', field('CS-FIELD-CONTRIBUTION-STATEMENT', 'text', true)],
  ['scholarly.result_direction', field('CS-FIELD-RESULT-DIRECTION', 'enum', true, { values: RESULT_DIRECTION })],

  /* ---- §2.5 recognition ------------------------------------------------------------------------------------- */
  ['recognition.recognition_type', field('CS-FIELD-RECOGNITION-TYPE', 'enum', false, { values: RECOGNITION_TYPE })],
  ['recognition.award_scope', field('CS-FIELD-AWARD-SCOPE', 'enum', true, { values: AWARD_SCOPE })],
  ['recognition.selection_basis_stated', field('CS-FIELD-SELECTION-BASIS', 'enum', true, { values: SELECTION_BASIS })],
  ['recognition.monetary_value', field('CS-FIELD-MONETARY-VALUE', 'text', true)],
  ['recognition.year', field('CS-FIELD-RECOGNITION-YEAR', 'integer', true)],

  /* ---- §2.6 clinical ---------------------------------------------------------------------------------------- */
  ['clinical.setting', field('CS-FIELD-CLINICAL-SETTING', 'text', true)],
  ['clinical.discipline', field('CS-FIELD-CLINICAL-DISCIPLINE', 'text', true)],
  ['clinical.exposure_type', field('CS-FIELD-EXPOSURE-TYPE', 'enum', true, { values: EXPOSURE_TYPE })],
  ['clinical.weeks_stated', field('CS-FIELD-WEEKS-STATED', 'number', true)],
  ['clinical.patient_contact_stated', field('CS-FIELD-PATIENT-CONTACT', 'enum', true, { values: PATIENT_CONTACT })],
  ['clinical.licence_status_at_time', field('CS-FIELD-LICENCE-STATUS', 'enum', true, { values: LICENCE_STATUS })],
]);

/**
 * §2.2a — one label per registered `signal_type`. Totality is a TEST, not a comment: `factLabels.test.ts` walks
 * `SIGNAL_TYPES` and fails on any member missing from this record, so the count here can never drift behind the
 * registry (as the codebook's own "20-value" prose already has, since v1.8 added `stated_progression_clause`).
 *
 * KEYED BY THE TYPE, NOT BY THE FIELD STRING, so the field spelling stays owned by `signalField()` — the same
 * single-constructor discipline that keeps signal fact ids from being invented at a read site.
 */
export const SIGNAL_LABELS: Readonly<Record<SignalType, CopyStringId>> = Object.freeze({
  participatory_function: 'CS-FIELD-SIGNAL-PARTICIPATORY-FUNCTION',
  bounded_object: 'CS-FIELD-SIGNAL-BOUNDED-OBJECT',
  ownership_relation: 'CS-FIELD-SIGNAL-OWNERSHIP-RELATION',
  direction_of_whole: 'CS-FIELD-SIGNAL-DIRECTION-OF-WHOLE',
  coordination_of_others: 'CS-FIELD-SIGNAL-COORDINATION-OF-OTHERS',
  stated_independent_practice: 'CS-FIELD-SIGNAL-STATED-INDEPENDENT-PRACTICE',
  outcome_or_recognition_stated: 'CS-FIELD-SIGNAL-OUTCOME-OR-RECOGNITION',
  d3_named_setting: 'CS-FIELD-SIGNAL-D3-NAMED-SETTING',
  d3_stated_count: 'CS-FIELD-SIGNAL-D3-STATED-COUNT',
  d3_named_deliverable: 'CS-FIELD-SIGNAL-D3-NAMED-DELIVERABLE',
  d3_named_audience: 'CS-FIELD-SIGNAL-D3-NAMED-AUDIENCE',
  d3_named_collaborating_group: 'CS-FIELD-SIGNAL-D3-NAMED-COLLABORATING-GROUP',
  describes_activity: 'CS-FIELD-SIGNAL-DESCRIBES-ACTIVITY',
  collaboration_function: 'CS-FIELD-SIGNAL-COLLABORATION-FUNCTION',
  domain_assignment_clinical: 'CS-FIELD-SIGNAL-DOMAIN-CLINICAL',
  domain_assignment_leadership: 'CS-FIELD-SIGNAL-DOMAIN-LEADERSHIP',
  domain_assignment_scholarship: 'CS-FIELD-SIGNAL-DOMAIN-SCHOLARSHIP',
  domain_assignment_service: 'CS-FIELD-SIGNAL-DOMAIN-SERVICE',
  domain_assignment_teaching: 'CS-FIELD-SIGNAL-DOMAIN-TEACHING',
  organization_context_token: 'CS-FIELD-SIGNAL-ORGANIZATION-CONTEXT',
  stated_progression_clause: 'CS-FIELD-SIGNAL-STATED-PROGRESSION',
});

/** The `signal.` prefix, spelled ONCE. `signalField()` builds it; this reads it back. */
export const SIGNAL_FIELD_PREFIX = 'signal.';

const SIGNAL_TYPE_SET: ReadonlySet<string> = new Set<string>(SIGNAL_TYPES);

/**
 * The presentation for one `field` string, or `null` when this build has no registered vocabulary for it.
 *
 * `null` IS A REAL ANSWER AND IT MEANS "DO NOT RENDER". The drift test asserts that every field the extractor
 * can mint, and every field the PATCH route accepts, resolves here — so `null` in production means a field
 * arrived from a NEWER backend than this bundle, and the honest response to that is the same one the rest of
 * this surface gives to content it cannot resolve: show nothing, rather than show an internal token.
 */
export function presentationFor(fieldName: string): FieldPresentation | null {
  if (fieldName.startsWith(SIGNAL_FIELD_PREFIX)) {
    const type = fieldName.slice(SIGNAL_FIELD_PREFIX.length);
    if (!SIGNAL_TYPE_SET.has(type)) return null;
    // A signal fact's value is a §2.2a boolean: the CV states this, or (assessed-negative) it does not.
    return field(SIGNAL_LABELS[type as SignalType], 'boolean', false);
  }
  return FIELD_PRESENTATION.get(fieldName) ?? null;
}

/** The registered label for one enum member, or `null` — an unregistered member never renders its raw token. */
export function enumLabelFor(fieldName: string, value: unknown): CopyStringId | null {
  const p = presentationFor(fieldName);
  if (p === null || p.kind !== 'enum' || p.values === undefined) return null;
  if (typeof value !== 'string') return null;
  return p.values[value] ?? null;
}

/** Is this field one whose stored form keeps `\n` (§1.5's newline contract)? */
export function isMultilineField(fieldName: string): boolean {
  return presentationFor(fieldName)?.multiline === true;
}
